import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Lightbulb,
  Shield,
  GraduationCap,
  Globe,
  Target,
  Rocket,
  Heart,
  CheckCircle,
  ArrowRight,
  Users,
  Zap,
  TrendingUp,
  Brain,
  Sparkles,
  Code2,
  Clock,
  Star,
} from "lucide-react";
import FooterNew from "../components/FooterNew";

const coreValues = [
  {
    icon: Lightbulb,
    title: "Innovation Excellence",
    color: "#6366F1",
    desc: "We don't just follow trends — we set them. Our R&D team constantly explores emerging AI paradigms to deliver tomorrow's solutions today.",
    points: [
      "Continuous research and development",
      "Cutting-edge technology adoption",
      "Forward-thinking solutions",
      "Pushing AI boundaries",
    ],
  },
  {
    icon: Heart,
    title: "Client Success",
    color: "#22D3EE",
    desc: "Your growth is our metric. We build long-term partnerships rooted in trust, transparency, and measurable business outcomes.",
    points: [
      "Solution-oriented approach",
      "Measurable business impact",
      "Long-term partnerships",
      "Transparent communication",
    ],
  },
  {
    icon: Shield,
    title: "Ethical AI",
    color: "#A78BFA",
    desc: "We champion responsible AI that is fair, explainable, and privacy-first — because powerful technology demands powerful accountability.",
    points: [
      "Responsible development",
      "Bias mitigation",
      "Data privacy first",
      "Transparent algorithms",
    ],
  },
];

const impactData = [
  { value: "25+", label: "AI Solutions Deployed", icon: Rocket },
  { value: "15+", label: "Industry Verticals", icon: Globe },
  { value: "50+", label: "AI Experts", icon: Users },
  { value: "100%", label: "Client Retention", icon: Star },
  { value: "24/7", label: "Support Uptime", icon: Clock },
  { value: "3M+", label: "Documents Processed", icon: Zap },
];

const timeline = [
  { year: "2020", title: "The Spark", desc: "Founded by a team of AI researchers with a bold mission — make enterprise-grade AI accessible to every business." },
  { year: "2021", title: "First Enterprise Wins", desc: "Deployed custom ML models for banking and healthcare clients; grew to a 15-member team." },
  { year: "2022", title: "Generative AI Leap", desc: "Early adopters of LLMs and RAG architectures; launched our first Generative AI product suite." },
  { year: "2023", title: "National Expansion", desc: "Scaled to 50+ AI experts, serving clients across 15+ industry verticals with 100% retention." },
  { year: "2024", title: "Global Footprint", desc: "Expanded operations internationally, processing 3M+ documents with AI-driven automation." },
  { year: "2025", title: "Industry Leader", desc: "Recognized as an AI innovation leader; launched next-gen agentic AI and multi-modal platforms." },
];

const whyUsCards = [
  {
    icon: Brain,
    title: "AI-Native Team",
    desc: "80% of our engineers hold advanced degrees in AI/ML. We live and breathe artificial intelligence.",
    color: "#6366F1",
  },
  {
    icon: Sparkles,
    title: "Generative AI Pioneers",
    desc: "Early adopters and builders of LLM, RAG, and agentic AI systems — not just integrators.",
    color: "#22D3EE",
  },
  {
    icon: Code2,
    title: "End-to-End Delivery",
    desc: "From ideation to production deployment and ongoing optimization — we own the full lifecycle.",
    color: "#A78BFA",
  },
  {
    icon: TrendingUp,
    title: "Measurable ROI",
    desc: "Every solution is benchmarked against business KPIs. We don't ship demos — we ship results.",
    color: "#F472B6",
  },
];

const glassCard = {
  background: "rgba(30, 41, 59, 0.4)",
  backdropFilter: "blur(12px)",
  border: "1px solid rgba(99, 102, 241, 0.1)",
};

const About = () => {
  return (
    <main className="bg-primary">
      {/* Hero */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <div
            className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full opacity-[0.12] blur-[100px]"
            style={{ background: "radial-gradient(circle, #6366F1, transparent)" }}
          />
          <div
            className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full opacity-[0.08] blur-[100px]"
            style={{ background: "radial-gradient(circle, #22D3EE, transparent)" }}
          />
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-[0.04] blur-[120px]"
            style={{ background: "radial-gradient(circle, #A78BFA, transparent)" }}
          />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-ai-glow text-sm font-medium mb-6">
              About CLIENTCODE AI
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight">
              We Build <span className="gradient-text">Intelligent Systems</span> That Transform Businesses
            </h1>
            <p className="text-lg sm:text-xl text-slate-400 max-w-3xl mx-auto mb-10 leading-relaxed">
              Since 2020, CLIENTCODE AI Innovation has been at the forefront of enterprise AI — empowering organizations with Generative AI, Machine Learning, and end-to-end intelligent automation that delivers real, measurable impact.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="neon-btn neon-btn-primary no-underline">
                <Rocket className="w-4 h-4" />
                Start Your AI Journey
              </Link>
              <Link to="/services" className="neon-btn neon-btn-outline no-underline">
                Explore Services
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Story with Timeline */}
      <section className="section-padding relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Our <span className="gradient-text">Journey</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg">
              From a bold idea in 2020 to an industry-leading AI company — here's how we got here.
            </p>
          </motion.div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px" style={{ background: "linear-gradient(180deg, #6366F1, #22D3EE, #A78BFA)" }} />

            <div className="space-y-8 md:space-y-0">
              {timeline.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: i % 2 === 0 ? -40 : 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className={`relative md:flex md:items-center md:mb-12 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}
                >
                  {/* Content */}
                  <div className={`md:w-[calc(50%-2rem)] ${i % 2 === 0 ? "md:pr-8 md:text-right" : "md:pl-8"}`}>
                    <div
                      className="rounded-2xl p-6 group hover:-translate-y-1 transition-transform duration-300"
                      style={glassCard}
                    >
                      <span className="text-sm font-bold gradient-text">{item.year}</span>
                      <h3 className="text-lg font-bold text-white mt-1 mb-2">{item.title}</h3>
                      <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                  {/* Dot */}
                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full border-2 border-accent bg-primary z-10" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="rounded-2xl p-8 sm:p-10 relative overflow-hidden"
              style={{ ...glassCard, border: "1px solid rgba(99, 102, 241, 0.15)" }}
            >
              <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10 blur-3xl" style={{ background: "#6366F1" }} />
              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-[#6366F1]/10 border border-[#6366F1]/30 flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-[#6366F1]" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Our Mission</h3>
                <p className="text-slate-400 leading-relaxed mb-6">
                  To democratize enterprise-grade AI for businesses of every size — transforming complex challenges into intelligent, scalable, and ethical solutions that drive measurable growth.
                </p>
                <div className="space-y-3">
                  {[
                    "Solve complex business challenges with AI",
                    "Deliver measurable ROI on every engagement",
                    "Make cutting-edge AI accessible and practical",
                    "Build long-term technology partnerships",
                  ].map((text, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-[#6366F1] flex-shrink-0" />
                      <span className="text-sm text-slate-300">{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="rounded-2xl p-8 sm:p-10 relative overflow-hidden"
              style={{ ...glassCard, border: "1px solid rgba(34, 211, 238, 0.15)" }}
            >
              <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10 blur-3xl" style={{ background: "#22D3EE" }} />
              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-[#22D3EE]/10 border border-[#22D3EE]/30 flex items-center justify-center mb-6">
                  <Sparkles className="w-7 h-7 text-[#22D3EE]" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Our Vision</h3>
                <p className="text-slate-400 leading-relaxed mb-6">
                  To be the most trusted AI innovation partner for enterprises worldwide — pioneering responsible, human-centric artificial intelligence that amplifies human potential and reshapes industries.
                </p>
                <div className="space-y-3">
                  {[
                    "Lead the next wave of AI-driven transformation",
                    "Set global standards for ethical AI development",
                    "Empower a million businesses with intelligent automation",
                    "Bridge the gap between AI research and real-world impact",
                  ].map((text, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-[#22D3EE] flex-shrink-0" />
                      <span className="text-sm text-slate-300">{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-ai-glow text-sm font-medium mb-4">
              Why CLIENTCODE AI?
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              What Sets Us <span className="gradient-text">Apart</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              We're not just another IT company that added "AI" to the name. Artificial intelligence is our DNA.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyUsCards.map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                whileHover={{ y: -8 }}
                className="rounded-2xl p-6 text-center group"
                style={glassCard}
              >
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform"
                  style={{ background: `${card.color}15`, border: `1px solid ${card.color}30` }}
                >
                  <card.icon className="w-7 h-7" style={{ color: card.color }} />
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{card.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Our Core <span className="gradient-text">Values</span>
            </h2>
            <p className="text-slate-400 max-w-lg mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {coreValues.map((val, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
                whileHover={{ y: -6 }}
                className="rounded-2xl p-8 group"
                style={glassCard}
              >
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform"
                  style={{ background: `${val.color}15`, border: `1px solid ${val.color}30` }}
                >
                  <val.icon className="w-7 h-7" style={{ color: val.color }} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{val.title}</h3>
                <p className="text-sm text-slate-400 mb-5 leading-relaxed">{val.desc}</p>
                <div className="w-10 h-0.5 mb-4 rounded-full" style={{ background: val.color }} />
                <ul className="space-y-2 list-none p-0 m-0">
                  {val.points.map((pt, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-slate-400">
                      <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: val.color }} />
                      {pt}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Numbers */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full opacity-[0.06] blur-[100px]" style={{ background: "linear-gradient(90deg, #6366F1, #22D3EE)" }} />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Our Impact in <span className="gradient-text">Numbers</span>
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Real metrics that reflect our commitment to delivering exceptional AI solutions.
            </p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {impactData.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
                whileHover={{ scale: 1.05 }}
                className="rounded-2xl p-6 text-center group"
                style={glassCard}
              >
                <div className="w-10 h-10 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                  <item.icon className="w-5 h-5 text-ai-glow" />
                </div>
                <div className="text-3xl sm:text-4xl font-bold gradient-text mb-2">{item.value}</div>
                <div className="text-sm text-slate-400">{item.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Our <span className="gradient-text">Expertise</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Our team comprises seasoned AI specialists, data scientists, and
              industry experts with diverse backgrounds.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: GraduationCap,
                title: "Technical Excellence",
                color: "#6366F1",
                items: [
                  "Advanced Degrees: 80% hold master's or PhDs in AI",
                  "Active contributions to AI research",
                  "Industry certifications across cloud & AI",
                  "Deep knowledge in GenAI, ML, NLP, CV",
                ],
              },
              {
                icon: Globe,
                title: "Industry Experience",
                color: "#22D3EE",
                items: [
                  "Cross-Industry: Finance, Healthcare, Retail & more",
                  "Global project experience",
                  "Understanding of tech & business outcomes",
                  "Led AI digital transformation initiatives",
                ],
              },
            ].map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
                whileHover={{ y: -4 }}
                className="rounded-2xl p-8"
                style={glassCard}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: `${card.color}15`, border: `1px solid ${card.color}30` }}
                  >
                    <card.icon className="w-6 h-6" style={{ color: card.color }} />
                  </div>
                  <h3 className="text-xl font-bold text-white">{card.title}</h3>
                </div>
                <ul className="space-y-3 list-none p-0 m-0">
                  {card.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-400">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: card.color }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-2xl p-8 sm:p-14 relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(34, 211, 238, 0.08))",
              border: "1px solid rgba(99, 102, 241, 0.2)",
            }}
          >
            <div className="absolute top-0 left-0 w-60 h-60 rounded-full opacity-10 blur-3xl" style={{ background: "#6366F1" }} />
            <div className="absolute bottom-0 right-0 w-60 h-60 rounded-full opacity-10 blur-3xl" style={{ background: "#22D3EE" }} />
            <div className="relative">
              <span className="inline-block px-4 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-ai-glow text-sm font-medium mb-6">
                Let's Build Together
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Ready to Transform Your Business with <span className="gradient-text">AI</span>?
              </h2>
              <p className="text-lg text-slate-400 mb-10 max-w-2xl mx-auto">
                Partner with a team that's as passionate about your success as you are.
                Let's turn your vision into intelligent reality.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact" className="neon-btn neon-btn-primary no-underline">
                  <Rocket className="w-4 h-4" />
                  Start a Conversation
                </Link>
                <Link to="/projects" className="neon-btn neon-btn-outline no-underline">
                  View Our Work
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <FooterNew />
    </main>
  );
};

export default About;
